import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import { useCloseGuard } from "@/hooks/use-close-guard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { BusyOverlay } from "@/components/common/BusyOverlay";

const MIN_LENGTH = 10;
const MAX_LENGTH = 500;

interface RejectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
  /**
   * Whether this dialog covers the whole page while the reject runs. A table that opens it passes
   * `false` and covers its own Card instead.
   */
  pageOverlay?: boolean;
}

export function RejectionModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
  pageOverlay = true,
}: RejectionModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  /**
   * A reject request is outstanding.
   *
   * `isLoading` is the parent's mutation state and only arrives on the NEXT render, so two
   * clicks in the same tick both see `false` and both reject the same payment. The second one
   * fails — the payment is no longer pending — and the operator is shown an error for a
   * rejection that actually went through.
   */
  const confirmInFlight = useRef(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
      confirmInFlight.current = false;
    }
  }, [open]);

  // This dialog does not own the request, so it has no `finally` to release the latch in —
  // the parent's mutation settling is the signal.
  useEffect(() => {
    if (!isLoading) {
      confirmInFlight.current = false;
    }
  }, [isLoading]);

  const reasonLength = reason.trim().length;
  const isValid = reasonLength >= MIN_LENGTH && reasonLength <= MAX_LENGTH;

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setReason(value);
    if (error) {
      const newError = validateReason(value);
      setError(newError);
    }
  };

  const validateReason = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return "Rejection reason is required.";
    if (trimmed.length < MIN_LENGTH)
      return `Reason must be at least ${MIN_LENGTH} characters.`;
    if (trimmed.length > MAX_LENGTH)
      return `Reason must not exceed ${MAX_LENGTH} characters.`;
    return null;
  };

  const handleConfirm = () => {
    if (confirmInFlight.current) return;

    const validationError = validateReason(reason);
    if (validationError) {
      setError(validationError);
      return;
    }

    confirmInFlight.current = true;
    onConfirm(reason.trim());
    // The reason is NOT cleared here. It used to be, which meant a rejection that failed —
    // network, a payment someone else had already decided — left the operator staring at an
    // empty box with their sentence gone. The close effect clears it once the dialog is
    // actually done with.
  };

  const guard = useCloseGuard({
    open,
    onOpenChange,
    isDirty: reason.trim().length > 0,
    isSubmitting: isLoading,
    onDiscard: () => {
      setReason("");
      setError(null);
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={guard.onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogDescription>
              Please provide a reason for rejecting this payment.
            </DialogDescription>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="rejection-reason">Rejection Reason *</Label>
                <span
                  className={`text-xs ${
                    reasonLength > MAX_LENGTH
                      ? "text-destructive-ink"
                      : "text-muted-foreground"
                  }`}
                >
                  {reasonLength}/{MAX_LENGTH}
                </span>
              </div>
              <Textarea
                id="rejection-reason"
                value={reason}
                onChange={handleReasonChange}
                placeholder="Explain why this payment is being rejected..."
                rows={4}
                disabled={isLoading}
                className={error ? "border-destructive" : ""}
                aria-invalid={!!error}
                aria-describedby={error ? "rejection-error" : undefined}
              />
              {error && (
                <p
                  id="rejection-error"
                  className="text-sm text-destructive-ink"
                  role="alert"
                >
                  {error}
                </p>
              )}
              {!error && reasonLength > 0 && reasonLength < MIN_LENGTH && (
                <p className="text-sm text-muted-foreground">
                  At least {MIN_LENGTH - reasonLength} more character
                  {MIN_LENGTH - reasonLength !== 1 ? "s" : ""} required.
                </p>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isLoading || !isValid}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject Payment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {guard.dialog}
      <BusyOverlay open={isLoading && pageOverlay} label="Rejecting payment…" />
    </>
  );
}
